import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Input, Collapse, Button, Spin, message } from 'antd'
import { SearchOutlined, MailOutlined } from '@ant-design/icons'
import { getTenantDirectCommonReplies } from '../../utils/api'
import TenantDirectQuestionModal from './TenantDirectQuestionModal'
import './tenantDirectModal.scss'

const { Panel } = Collapse
const PAGE_SIZE = 5

const pickField = (item, keys) => {
    if (!item || typeof item !== 'object') return ''
    for (let i = 0; i < keys.length; i += 1) {
        const val = item[keys[i]]
        if (val != null && String(val).trim() !== '') {
            return String(val).trim()
        }
    }
    return ''
}

const extractReplyList = (payload, depth = 0) => {
    if (payload == null || depth > 3) return []
    if (typeof payload === 'string') {
        try {
            return extractReplyList(JSON.parse(payload), depth + 1)
        } catch (e) {
            return []
        }
    }
    if (Array.isArray(payload)) return payload
    if (typeof payload !== 'object') return []

    const listKeys = [
        'pageContent', 'list', 'records', 'rows', 'content', 'dataList', 'commonReplies',
        'items', 'result', 'data', 'commonRepliesVOList', 'replyList',
    ]
    for (let i = 0; i < listKeys.length; i += 1) {
        const val = payload[listKeys[i]]
        if (Array.isArray(val)) return val
        if (val && typeof val === 'object') {
            const nested = extractReplyList(val, depth + 1)
            if (nested.length) return nested
        }
    }
    return []
}

const normalizeReplies = (payload) => {
    const list = extractReplyList(payload)
    return list.map((item, index) => ({
        id: item.id != null ? item.id : (item.replyId != null ? item.replyId : `${index}`),
        question: pickField(item, ['description']),
        answer: pickField(item, ['resolveFeedback']),
    })).filter((item) => item.question)
}

const parseHasMore = (payload, page, pageSize, listLength) => {
    const meta = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null
    if (meta) {
        const total = meta.total ?? meta.totalCount ?? meta.count ?? meta.totalElements ?? meta.totalSize
        if (total != null && !Number.isNaN(Number(total))) {
            return page * pageSize < Number(total)
        }
        const pages = meta.pages ?? meta.totalPages
        if (pages != null && !Number.isNaN(Number(pages))) {
            return page < Number(pages)
        }
    }
    return listLength >= pageSize
}

const renderExpandIcon = ({ isActive }) => (
    <span className={`tenant-direct-expand-icon ${isActive ? 'is-active' : ''}`} />
)

const TenantDirectModal = ({
    visible,
    loggedIn,
    userInfo,
    onClose,
    onLogin,
    onMyDirect,
}) => {
    const [questionVisible, setQuestionVisible] = useState(false)
    const [keyword, setKeyword] = useState('')
    const [searchKeyword, setSearchKeyword] = useState('')
    const [replyList, setReplyList] = useState([])
    const [currentPage, setCurrentPage] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [activeKey, setActiveKey] = useState([])
    const searchTimerRef = useRef(null)
    const loadingRef = useRef(false)
    const openedRef = useRef(false)

    const fetchReplies = useCallback(async ({ page = 1, reset = false, keywordValue = '' } = {}) => {
        if (!loggedIn || loadingRef.current) return
        loadingRef.current = true
        if (reset) {
            setLoading(true)
        } else {
            setLoadingMore(true)
        }
        try {
            const params = {
                current: page,
                pageIndex: page - 1,
                pageNum: page,
                pageSize: PAGE_SIZE,
            }
            const key = keywordValue.trim()
            if (key) {
                params.description = key
            }
            const res = await getTenantDirectCommonReplies(params)
            if (!res || Number(res.code) !== 0) {
                message.error((res && res.message) || '获取常见问题失败')
                if (reset) {
                    setReplyList([])
                    setHasMore(false)
                    setCurrentPage(0)
                    setActiveKey([])
                }
                return
            }
            const pageList = normalizeReplies(res.data)
            setReplyList((prev) => (reset ? pageList : [...prev, ...pageList]))
            setCurrentPage(page)
            setHasMore(parseHasMore(res.data, page, PAGE_SIZE, pageList.length))
            if (reset) {
                setActiveKey([])
            }
        } catch (e) {
            if (reset) {
                setReplyList([])
                setHasMore(false)
                setCurrentPage(0)
                setActiveKey([])
            }
            message.error('获取常见问题失败')
        } finally {
            loadingRef.current = false
            setLoading(false)
            setLoadingMore(false)
        }
    }, [loggedIn])

    useEffect(() => {
        if (!visible) {
            openedRef.current = false
            return undefined
        }
        if (!openedRef.current) {
            openedRef.current = true
            setKeyword('')
            setSearchKeyword('')
            setReplyList([])
            setCurrentPage(0)
            setHasMore(false)
            setActiveKey([])
            setLoading(false)
            setLoadingMore(false)
        }
        if (!loggedIn) {
            return () => {
                if (searchTimerRef.current) {
                    clearTimeout(searchTimerRef.current)
                }
            }
        }
        fetchReplies({ page: 1, reset: true, keywordValue: searchKeyword })
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current)
            }
        }
    }, [visible, loggedIn, searchKeyword, fetchReplies])

    const handleKeywordChange = (e) => {
        if (!loggedIn) return
        const value = e.target.value
        setKeyword(value)
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }
        searchTimerRef.current = setTimeout(() => {
            setSearchKeyword(value.trim())
        }, 300)
    }

    const showEmptyState = !loading && replyList.length === 0
    const showLoggedInEmpty = loggedIn && showEmptyState

    const handleClose = () => {
        setKeyword('')
        setSearchKeyword('')
        setReplyList([])
        setCurrentPage(0)
        setHasMore(false)
        setActiveKey([])
        onClose?.()
    }

    const loadMore = () => {
        if (!loggedIn || !hasMore || loading || loadingMore) return
        fetchReplies({
            page: currentPage + 1,
            reset: false,
            keywordValue: searchKeyword,
        })
    }

    const handleListScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        if (scrollHeight - scrollTop - clientHeight < 48) {
            loadMore()
        }
    }

    const renderListContent = () => {
        if (loading && !replyList.length) {
            return <div className="tenant-direct-modal__empty">加载中...</div>
        }

        if (showLoggedInEmpty) {
            return (
                <div className="tenant-direct-modal__empty-state">
                    <MailOutlined className="tenant-direct-modal__empty-icon" />
                    <div className="tenant-direct-modal__empty-title">未找到相关问题</div>
                    <div className="tenant-direct-modal__empty-desc">
                        您可以点击下方「新建问题」向我们反馈
                    </div>
                </div>
            )
        }

        if (replyList.length) {
            return (
                <>
                    <Collapse
                        bordered={false}
                        expandIconPosition="end"
                        expandIcon={renderExpandIcon}
                        className="tenant-direct-collapse"
                        activeKey={activeKey}
                        onChange={(keys) => setActiveKey(Array.isArray(keys) ? keys : [keys])}
                    >
                        {replyList.map((item) => (
                            <Panel
                                key={item.id}
                                header={(
                                    <span className="tenant-direct-q-item">
                                        <span className="tenant-direct-q-icon">Q</span>
                                        <span className="tenant-direct-q-text" title={item.question}>
                                            {item.question}
                                        </span>
                                    </span>
                                )}
                            >
                                {item.answer || '暂无回复'}
                            </Panel>
                        ))}
                    </Collapse>
                    {loadingMore ? (
                        <div className="tenant-direct-modal__load-more">加载中...</div>
                    ) : null}
                    {!hasMore && replyList.length > 0 ? (
                        <div className="tenant-direct-modal__load-more tenant-direct-modal__load-more--done">没有更多了</div>
                    ) : null}
                </>
            )
        }

        return (
            <div className="tenant-direct-modal__empty">
                暂无相关问题
            </div>
        )
    }

    return (
        <>
        <Modal
            title="租情直通车"
            visible={visible}
            onCancel={handleClose}
            footer={null}
            width={720}
            destroyOnClose
            className="tenant-direct-modal-wrap"
        >
            <div className="tenant-direct-modal">
                {!loggedIn ? (
                    <>
                        <div className="tenant-direct-modal__subtitle">常见问题解答</div>
                        <div className="tenant-direct-modal__tip">
                            查看「我的租情直通车」或提交新问题需先{' '}
                            <a
                                onClick={(e) => {
                                    e.preventDefault()
                                    onLogin?.()
                                }}
                            >
                                登录
                            </a>{' '}
                            租户账号
                        </div>
                    </>
                ) : null}
                <div className="tenant-direct-modal__search">
                    <Input
                        allowClear
                        disabled={!loggedIn}
                        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                        placeholder="搜索问题关键词"
                        value={keyword}
                        onChange={handleKeywordChange}
                    />
                </div>
                {loggedIn ? (
                    <Spin spinning={loading && !!replyList.length}>
                        <div
                            className={`tenant-direct-modal__list ${showLoggedInEmpty ? 'tenant-direct-modal__list--empty' : ''}`}
                            onScroll={handleListScroll}
                        >
                            {renderListContent()}
                        </div>
                    </Spin>
                ) : null}
                <div className="tenant-direct-modal__footer">
                    <Button
                        disabled={!loggedIn}
                        onClick={() => {
                            if (!loggedIn) return
                            onMyDirect?.()
                        }}
                    >
                        我的租情直通车
                    </Button>
                    <Button
                        type="primary"
                        className="tenant-direct-btn-primary"
                        onClick={() => {
                            if (!loggedIn) {
                                onLogin?.()
                                return
                            }
                            setQuestionVisible(true)
                        }}
                    >
                        {loggedIn ? '新建问题' : '登录后提问'}
                    </Button>
                </div>
            </div>
        </Modal>
        <TenantDirectQuestionModal
            visible={questionVisible}
            userInfo={userInfo}
            onClose={() => setQuestionVisible(false)}
        />
        </>
    )
}

export default TenantDirectModal
